/**
 * HoverPopoverCard — common skeleton for hover popovers in PlanViewer InfoMode.
 *
 * Includes: portal + cursor-follow position (delegated to <Popover>),
 * .card surface, .header with title, optional DataGrid or any children.
 *
 * The content (header + items / children) is built by the consumer — this component is UI-only,
 * without knowledge of the element type (compartment / window / equipment / partition).
 */

import { DataGrid } from '../../../../shared/ui/DataGrid/index.js'
import { Popover }  from '../../../../shared/ui/Popover/index.js'

import styles from './HoverPopoverCard.module.scss'

/**
 * @param {object}                   props
 * @param {boolean}                  props.isOpen
 * @param {{x:number,y:number}|null} props.position
 * @param {function}                 [props.onClose]
 * @param {string}                   props.header
 * @param {Array<{label:string,value:string}>} [props.items]  — when provided, rendered as DataGrid
 * @param {number}                   [props.maxColumns=3]      — limit DataGrid columns
 * @param {React.ReactNode}          [props.children]          — alternative to items (e.g., EquipmentPopover .dimensions)
 */
export function HoverPopoverCard({ isOpen, position, onClose, header, items, maxColumns = 3, children }) {
  return (
    <Popover isOpen={isOpen} initialCursorPos={position} onClose={onClose}>
      <div className={styles.card}>
        <div className={styles.header}>{header}</div>
        {items && items.length > 0 && (
          <DataGrid items={items} columns={Math.min(items.length, maxColumns)} size="small" />
        )}
        {children}
      </div>
    </Popover>
  )
}
